import { GetMinimalLists, addItems, createList } from 'api/list';
import { MinimalList } from 'api/list/graphqlTypes';
import { reportError } from 'utils/sentry';
import { country } from 'utils/settings';
import translate from 'utils/translate';

type FavouritesPayload = {
    favouritedItems?: string[];
    savedForLaterItems?: string[];
};

type Callback = (data: FavouritesPayload) => void;

export const FAVOURITES_STORAGE_KEY = `favourites-state-${country}`;
export const SAVED_FOR_LATER_STORAGE_KEY = `saved-for-later-state-${country}`;

const favouritesListeners: Callback[] = [];

/** Helpers */
const getItemsFromCache = (storageKey: string): string[] => {
    try {
        const stored = localStorage?.getItem(storageKey);
        return stored?.length ? JSON.parse(stored) : [];
    } catch (e) {
        reportError(e as Error);
    }
    return [];
};

/** subscribe to changes in the stored data */
export const subscribeFavourites = (callback: Callback) => {
    favouritesListeners.push(callback);
    const favourites = getItemsFromCache(FAVOURITES_STORAGE_KEY);
    const savedForLater = getItemsFromCache(SAVED_FOR_LATER_STORAGE_KEY);

    if (favourites.length || savedForLater.length) {
        callback({
            ...(favourites && { favouritedItems: favourites }),
            ...(savedForLater && { savedForLaterItems: savedForLater }),
        });
    }
};

export const updateMultiListStore = (lists: MinimalList[]) => {
    const items: string[] = [];
    lists.forEach(list => {
        list.items.forEach(item => items.push(item.itemNo));
        list.designs.forEach(design => items.push(design.configurationId));
    });
    window.localStorage?.setItem(FAVOURITES_STORAGE_KEY, JSON.stringify(items));
    favouritesListeners.forEach(cb => cb({ favouritedItems: items }));
};

export const updateSavedForLaterStore = (items: string[]) => {
    window.localStorage?.setItem(
        SAVED_FOR_LATER_STORAGE_KEY,
        JSON.stringify(items)
    );
    favouritesListeners.forEach(cb => cb({ savedForLaterItems: items }));
};

export const refreshCache = async () => {
    const response = await GetMinimalLists();
    updateMultiListStore(response.data.lists);
    const savedForLaterList = response.data.lists.find(
        list => list.name === translate('SAVED_FOR_LATER_LIST_NAME')
    );
    const savedForLaterItems = [
        ...(savedForLaterList?.items.map(item => item.itemNo) || []),
        ...(savedForLaterList?.designs.map(design => design.configurationId) ||
            []),
    ];
    updateSavedForLaterStore(savedForLaterItems);
};

// If user has logged in and has items in saved for later, create a new list and add items to it
export const mergeSavedForLaterData = async () => {
    const cachedSavedForLaterItems = getItemsFromCache(
        SAVED_FOR_LATER_STORAGE_KEY
    );

    if (!cachedSavedForLaterItems.length) {
        return;
    }

    const response = await GetMinimalLists();
    let savedForLaterListId = response.data.lists.find(
        list => list.name === translate('SAVED_FOR_LATER_LIST_NAME')
    )?.listId;

    if (!savedForLaterListId) {
        const createdList = await createList({
            name: translate('SAVED_FOR_LATER_LIST_NAME'),
        });
        savedForLaterListId = createdList.data.list.listId;
    }

    const items = cachedSavedForLaterItems.map(itemNo => ({
        itemNo,
        quantity: 1,
    }));

    // Silently add items to saved for later list
    await addItems({
        items,
        listId: savedForLaterListId,
    });
};

export const clearFavouritesStore = () =>
    localStorage?.removeItem(FAVOURITES_STORAGE_KEY);
